import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetMyStudentDetailQuery,
  useLazyGetMyStudentMassAttendanceQuery,
} from '../../api/enrollment';
import { CourseChip } from '../../components/CourseChip';
import { CourseChatLink } from '../Search/components/CourseChatLink';
import { parseStudentIdFromUrl } from '../../utils/qr-code';

const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const getDefaultDateRange = (): { fromDate: string; toDate: string } => {
  const today = new Date();
  const currentMonth = today.getMonth();

  if (currentMonth >= 8 || currentMonth <= 5) {
    const year = currentMonth >= 8 ? today.getFullYear() : today.getFullYear() - 1;
    const fromDate = new Date(year, 8, 1);
    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(today),
    };
  }

  const fromDate = new Date(today);
  fromDate.setMonth(fromDate.getMonth() - 3);
  const toDate = new Date(today);
  toDate.setMonth(toDate.getMonth() + 3);
  return {
    fromDate: formatDate(fromDate),
    toDate: formatDate(toDate),
  };
};

const formatDisplayDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const getDayOfWeek = (dateStr: string): string => {
  const date = new Date(dateStr);
  const days = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
  return days[date.getDay()];
};

const getMonthYearKey = (dateStr: string): string => {
  const [year, month] = dateStr.split('-');
  return `${month}-${year}`;
};

const getMonthYearLabel = (key: string): string => {
  const [month, year] = key.split('-');
  const monthNames = [
    'Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6',
    'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12',
  ];
  return `${monthNames[parseInt(month, 10) - 1]}, ${year}`;
};

interface GroupedAttendance {
  [key: string]: { massDate: string; massStartTime: string }[];
}

const groupByMonth = (data: { massDate: string; massStartTime: string }[]): GroupedAttendance => {
  return data.reduce((acc, item) => {
    const key = getMonthYearKey(item.massDate);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as GroupedAttendance);
};

const normalizeMassDate = (massDate: string): string => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(massDate)) {
    return massDate;
  }
  return massDate.split('T')[0];
};

const StudentDetail = () => {
  const navigate = useNavigate();
  const { id: rawId } = useParams<{ id: string }>();
  const isMobile = useMediaQuery('(max-width:768px)');

  const studentId = React.useMemo(() => parseStudentIdFromUrl(rawId), [rawId]);
  const numericStudentId = studentId ? Number(studentId) : 0;

  const defaultRange = React.useMemo(() => getDefaultDateRange(), []);
  const [fromDate, setFromDate] = React.useState(defaultRange.fromDate);
  const [toDate, setToDate] = React.useState(defaultRange.toDate);
  const [showMassHistory, setShowMassHistory] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);

  const {
    data: student,
    isFetching: isFetchingStudent,
    error: studentError,
  } = useGetMyStudentDetailQuery(numericStudentId, { skip: !numericStudentId });

  const [fetchMassAttendance, {
    data: massAttendance,
    isFetching: isFetchingMass,
    isUninitialized: isMassUninitialized,
  }] = useLazyGetMyStudentMassAttendanceQuery();

  React.useEffect(() => {
    const shouldFetch = isMobile ? activeTab === 1 : showMassHistory;
    if (shouldFetch && numericStudentId && fromDate && toDate) {
      fetchMassAttendance({ studentId: numericStudentId, fromDate, toDate });
    }
  }, [showMassHistory, activeTab, isMobile, numericStudentId, fromDate, toDate, fetchMassAttendance]);

  const normalizedMassAttendance = React.useMemo(
    () =>
      (massAttendance ?? []).map((item) => ({
        massDate: normalizeMassDate(item.massDate),
        massStartTime: item.massStartTime,
      })),
    [massAttendance]
  );

  const handleShowMassHistory = () => {
    setShowMassHistory(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleGoBack = () => {
    navigate('/home');
  };

  const courseName =
    student?.currentYearCourse && student.currentYearCourse !== '-'
      ? student.currentYearCourse
      : undefined;

  if (isFetchingStudent) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!studentId || studentError || !student) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={0} sm={2}></Grid>
        <Grid item xs={12} sm={8}>
          <Stack margin={4} spacing={3}>
            <Typography variant="h5">Không tìm thấy đoàn sinh</Typography>
            <Typography variant="body1" color="text.secondary">
              {!studentId
                ? 'Mã QR không hợp lệ.'
                : `Đoàn sinh với ID "${studentId}" không tồn tại hoặc đã bị xóa.`}
            </Typography>
            <Button variant="contained" onClick={handleGoBack}>
              Quay về trang chủ
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={0} sm={2}></Grid>
      </Grid>
    );
  }

  const DetailContent = () => (
    <>
      {courseName ? (
        <div>
          <CourseChip courseName={courseName} />
        </div>
      ) : null}

      <section>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Đoàn sinh
        </Typography>
        <Typography variant="h6" fontWeight="bold">
          {student.canonicalSaintName} {student.fullName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {student.dob}
        </Typography>
      </section>

      <Divider />

      <section>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Giáo lý viên
        </Typography>
        {!student.teachers?.length ? (
          <Typography variant="body2" color="text.secondary">
            Danh sách GLV sẽ được cập nhật sau.
          </Typography>
        ) : (
          <ol style={{ paddingLeft: '20px', margin: 0 }}>
            {student.teachers.map((teacher) => (
              <li key={teacher.id}>
                {teacher.teacherTitle} {teacher.canonicalSaintName}{' '}
                <b>{teacher.fullName}</b>
                {teacher.phone ? (
                  <>
                    {' '}
                    - <a href={`tel:${teacher.phone}`}>{teacher.phone}</a>
                  </>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <CourseChatLink courseName={courseName} url={student.zaloGroupUrl} />
    </>
  );

  const MassAttendanceContent = () => (
    <section>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={2}
        style={{ marginBottom: '16px' }}
      >
        <TextField
          label="Từ ngày"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth={isMobile}
        />
        <TextField
          label="Đến ngày"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth={isMobile}
        />
      </Stack>

      {isFetchingMass || isMassUninitialized ? (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tổng: <b>{normalizedMassAttendance.length}</b> lần
          </Typography>

          {normalizedMassAttendance.length > 0 ? (
            (() => {
              const grouped = groupByMonth(normalizedMassAttendance);
              const sortedKeys = Object.keys(grouped).sort((a, b) => {
                const [monthA, yearA] = a.split('-').map(Number);
                const [monthB, yearB] = b.split('-').map(Number);
                return yearB - yearA || monthB - monthA;
              });

              return (
                <Box>
                  {sortedKeys.map((monthKey) => (
                    <Accordion key={monthKey} defaultExpanded={sortedKeys.indexOf(monthKey) === 0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography fontWeight="medium">
                          {getMonthYearLabel(monthKey)} ({grouped[monthKey].length} lần)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {isMobile ? (
                          <Stack spacing={1.5}>
                            {grouped[monthKey].map((item, index) => {
                              const [, , day] = item.massDate.split('-');
                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    padding: '12px',
                                    borderRadius: '12px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #e0e0e0',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: '48px',
                                      textAlign: 'center',
                                      padding: '8px',
                                      borderRadius: '8px',
                                      backgroundColor: '#1976d2',
                                      color: 'white',
                                    }}
                                  >
                                    <Typography variant="h6" fontWeight="bold" lineHeight={1}>
                                      {day}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" fontWeight="medium">
                                      {getDayOfWeek(item.massDate)}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        color: 'text.secondary',
                                        mt: 0.5,
                                      }}
                                    >
                                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                                      <Typography variant="body2">
                                        {item.massStartTime}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        ) : (
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><b>Ngày</b></TableCell>
                                  <TableCell><b>Thứ</b></TableCell>
                                  <TableCell><b>Giờ lễ</b></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {grouped[monthKey].map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{formatDisplayDate(item.massDate)}</TableCell>
                                    <TableCell>{getDayOfWeek(item.massDate)}</TableCell>
                                    <TableCell>{item.massStartTime}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              );
            })()
          ) : (
            <p>Không có dữ liệu đi lễ trong khoảng thời gian này.</p>
          )}
        </>
      )}
    </section>
  );

  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          }}
        >
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Chi tiết" />
            <Tab label="Đi lễ" />
          </Tabs>
        </Box>
        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <Stack spacing={3}>
              <DetailContent />
              <Divider />
              <Button variant="outlined" onClick={handleGoBack}>
                Quay về trang chủ
              </Button>
            </Stack>
          )}
          {activeTab === 1 && (
            <Stack spacing={3}>
              <MassAttendanceContent />
            </Stack>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <Stack margin={4} spacing={3}>
          <DetailContent />

          <Divider />

          {!showMassHistory ? (
            <Button variant="text" onClick={handleShowMassHistory}>
              Lịch sử đi lễ
            </Button>
          ) : (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Đi lễ
              </Typography>
              <MassAttendanceContent />
            </>
          )}

          <Divider />

          <div>
            <Button variant="outlined" onClick={handleGoBack}>
              Quay về trang chủ
            </Button>
          </div>
        </Stack>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
    </Grid>
  );
};

export default StudentDetail;
